import { FC, CSSProperties } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Grid,
  GridItem,
  IconButton,
  ListItem,
  Stack,
  Tooltip,
  useBoolean,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverAnchor,
} from '@chakra-ui/react';
import { AddIcon, DragHandleIcon } from '@chakra-ui/icons';
import { useSortable } from '@dnd-kit/sortable';
import { OriginalMenu } from 'types/menu';
import { MenuCategory } from 'types/menuCategory';
import { TextWithTooltip } from 'components/molecules/TextWithTooltip';

type Props = {
  /** メニュー */
  item: OriginalMenu;
  /** メニュー追加 */
  handleClickAddMenu: (categoryId: MenuCategory['id']) => void;
  /** メニューの行クリック */
  handleClickMenuRow: (menu: OriginalMenu) => void;
  /** メニュー並び替え実行前の popover 表示状態チェック */
  checkOpenStateSortPopoverMenu: (id: OriginalMenu['id']) => boolean;
  /** メニュー並び替え実行 */
  handleCancelSortMenu: () => void;
  /** メニュー並び替え実行キャンセル */
  handleSortMenu: () => void;
};

export const MenuCategoryNestedListItem: FC<Props> = ({
  item,
  handleClickAddMenu,
  handleClickMenuRow,
  checkOpenStateSortPopoverMenu,
  handleCancelSortMenu,
  handleSortMenu,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    position: 'relative',
  };

  const [hover, setHover] = useBoolean();

  return (
    <ListItem
      listStyleType="none"
      onMouseEnter={setHover.on}
      onMouseLeave={setHover.off}
      pl="16px"
      pr={0}
      py="8px"
      borderTopWidth="1px"
      borderStyle="solid"
      borderColor="gray.200"
      ref={setNodeRef}
      style={style}
    >
      <Box
        m={0}
        w="100%"
        h="100%"
        textAlign="start"
        border="none"
        color="primaryList.item.color"
      >
        <Grid
          templateColumns="0.5fr 1fr 2fr 1fr 3fr 2fr 3fr"
          gap="10px"
          justifyItems="start"
          alignItems="center"
        >
          <GridItem />
          <GridItem />
          <GridItem w="100%">
            <Stack spacing="4px" direction="row" justifyContent="flex-end">
              <Tooltip label="メニューを追加する">
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    handleClickAddMenu(item.categoryId);
                  }}
                  size="xs"
                  aria-label="メニュー追加"
                  icon={<AddIcon color="white" />}
                  colorScheme="blue"
                  visibility={hover ? 'visible' : 'hidden'}
                />
              </Tooltip>
              <Tooltip label="メニューを並び替える">
                <IconButton
                  size="xs"
                  aria-label="メニュー並び替え"
                  icon={<DragHandleIcon />}
                  {...listeners}
                  {...attributes}
                  cursor={isDragging ? 'grabbing' : 'grab'}
                  visibility={hover ? 'visible' : 'hidden'}
                />
              </Tooltip>
            </Stack>
          </GridItem>
          <GridItem>
            <TextWithTooltip text={item.id.toString()} />
          </GridItem>
          <GridItem minW="100%" maxW="100%">
            <Popover
              returnFocusOnClose={false}
              isOpen={checkOpenStateSortPopoverMenu(item.id)}
              placement="right"
              closeOnBlur={false}
              variant="darkShadow"
              strategy="fixed"
            >
              <PopoverAnchor>
                <Button
                  display="inline-block"
                  w="100%"
                  textAlign="left"
                  variant="link"
                  onClick={() => handleClickMenuRow(item)}
                >
                  <TextWithTooltip
                    text={item.name}
                    textMaxWidth="300px"
                    visibleTooltip
                    tooltipMaxWidth="600px"
                    display="block"
                  />
                </Button>
              </PopoverAnchor>
              <PopoverContent ml="16px">
                <PopoverArrow />
                <PopoverBody>並び替えを反映します</PopoverBody>
                <PopoverFooter display="flex" justifyContent="flex-end">
                  <ButtonGroup size="sm">
                    <Button
                      variant="outline"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleCancelSortMenu();
                      }}
                    >
                      元に戻す
                    </Button>
                    <Button
                      colorScheme="blackAlpha"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSortMenu();
                      }}
                    >
                      反映
                    </Button>
                  </ButtonGroup>
                </PopoverFooter>
              </PopoverContent>
            </Popover>
          </GridItem>
          <GridItem>
            <TextWithTooltip text={item.type} />
          </GridItem>
          <GridItem minW="100%" maxW="100%">
            <TextWithTooltip
              text={item.description}
              textMaxWidth="300px"
              visibleTooltip
              tooltipMaxWidth="600px"
            />
          </GridItem>
        </Grid>
      </Box>
    </ListItem>
  );
};
